import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect } from 'react';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { ensureHexColor } from '@sorare/core/src/lib/colors';

import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';
import { useBuyCardPack } from '#src/hooks/packs/useBuyCardPack.ts';

import { BuyCardPackFlow } from './BuyCardFlow/index.tsx';
import type {
  BuyCardPackContent_cardPack,
  BuyCardPackDialogQuery,
  BuyCardPackDialogQueryVariables,
} from './__generated__/index.graphql.ts';

type Props = {
  cardPack: BuyCardPackContent_cardPack;
};
export const BuyCardPackContent = ({ cardPack }: Props) => {
  const { pictureUrl } = cardPack;

  const closeDialog = useCloseDialog();
  const handleClose = () => closeDialog(DialogKey.cardPack);

  const {
    buyCardPackData,
    onPurchase,
    onMobilePurchase,
    useNativeIOSPackOpening,
    packNotBought,
  } = useBuyCardPack(cardPack?.slug);

  useEffect(() => {
    if (useNativeIOSPackOpening) {
      onMobilePurchase();
    }
  }, [cardPack.slug, onMobilePurchase, useNativeIOSPackOpening]);

  if (useNativeIOSPackOpening) {
    return null;
  }

  return (
    <>
      {packNotBought ? (
        <BuyCardPackFlow
          onClose={handleClose}
          cardPack={cardPack}
          onPurchase={onPurchase}
        />
      ) : (
        <RevealPackCardsDialog
          open
          onClose={handleClose}
          cards={buyCardPackData!.cards!}
          onContinue={handleClose}
          cardSetSlug={cardPack.cardSet?.slug || ''}
          packId={buyCardPackData!.pack!.id!}
          packPictureUrl={pictureUrl}
          primaryColor={ensureHexColor(cardPack.primaryColor)}
          secondaryColor={ensureHexColor(cardPack.secondaryColor)}
          showPlayerDetails
        />
      )}
    </>
  );
};

BuyCardPackContent.fragments = {
  cardPack: gql`
    fragment BuyCardPackContent_cardPack on CardPack {
      slug
      pictureUrl
      cardSet {
        slug
      }
      ...BuyCardPackFlow_cardPack
    }
    ${BuyCardPackFlow.fragments.cardPack}
  ` as TypedDocumentNode<BuyCardPackContent_cardPack>,
};

const QUERY = gql`
  query BuyCardPackDialogQuery($cardPackSlug: String!) {
    market {
      id
      cardPack(slug: $cardPackSlug) {
        slug
        ...BuyCardPackContent_cardPack
      }
    }
  }
  ${BuyCardPackContent.fragments.cardPack}
` as TypedDocumentNode<BuyCardPackDialogQuery, BuyCardPackDialogQueryVariables>;

export const BuyCardPackDialog = () => {
  const cardPackSlug = useDialogParam(DialogKey.cardPack);
  const { data } = useQuery(QUERY, {
    variables: { cardPackSlug: cardPackSlug! },
    skip: !cardPackSlug,
  });
  const cardPack = data?.market.cardPack;
  if (!cardPack) {
    return null;
  }
  return <BuyCardPackContent cardPack={cardPack} />;
};
